import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { useReducedMotion } from '@/platform/design-system/hooks/useReducedMotion';
import { MOTION_TOKENS } from './motion/semantic';

interface PageTransitionProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'instant' | 'assemble';
}

const containerVariants = {
  assemble: {
    hidden: { opacity: 0, y: 8 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: MOTION_TOKENS.duration.enter,
        ease: MOTION_TOKENS.ease.enter,
        staggerChildren: MOTION_TOKENS.performance.maxStaggerMs / 1000,
      },
    },
  },
};

export function PageTransition({ children, className, variant = 'default' }: PageTransitionProps) {
  const prefersReduced = useReducedMotion();
  const effectiveVariant = prefersReduced ? 'instant' : variant;

  if (effectiveVariant === 'instant') {
    return <div className={className}>{children}</div>;
  }

  if (effectiveVariant === 'assemble') {
    return (
      <motion.div
        variants={containerVariants.assemble}
        initial="hidden"
        animate="visible"
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  // default: fade + slide up
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: MOTION_TOKENS.duration.enter, ease: MOTION_TOKENS.ease.enter }}
      className={clsx(className)}
    >
      {children}
    </motion.div>
  );
}
